import React from 'react';
import { InlineLoadingState } from '../../../../components/LoadingSpinner';
import { SettingsCard } from '../../components/SettingsCard';
import { SettingsCardSavedStatus } from '../../components/SettingsCardSavedStatus';
import { SettingsToggle } from '../../components/SettingsToggle';
import { SETTINGS_FIELD_LABEL } from '../../utils/settingsSectionStyles';

export function InboxReplySettingsCard() {
  const [loaded, setLoaded] = React.useState(false);
  const [enabled, setEnabled] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    fetch('/api/settings/inbox-reply', { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error((d as { error?: string }).error || 'Einstellungen konnten nicht geladen werden.');
        return d as { reply_quote_enabled?: boolean };
      })
      .then((d) => {
        if (cancelled) return;
        setEnabled(Boolean(d.reply_quote_enabled));
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (nextEnabled: boolean) => {
    const previous = enabled;
    setEnabled(nextEnabled);
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/inbox-reply', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reply_quote_enabled: nextEnabled }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen');
      setEnabled(Boolean((d as { reply_quote_enabled?: boolean }).reply_quote_enabled));
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      setEnabled(previous);
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Antworten"
      description="Optional den bisherigen Mailverlauf unter neue Antworten anhängen."
      actions={<SettingsCardSavedStatus saved={saved} />}
    >
      {error ? (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      ) : null}

      {!loaded ? (
        <InlineLoadingState label="Lade Einstellungen…" className="py-6" />
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className={SETTINGS_FIELD_LABEL}>Originalmail beim Antworten zitieren</p>
            <p className="text-sm text-app-text-muted mt-1">
              Hängt Absender, Betreff und Inhalt der beantworteten Mail unter die neue Nachricht.
            </p>
          </div>
          <SettingsToggle
            active={enabled}
            disabled={saving}
            onClick={() => void save(!enabled)}
            label="Originalmail beim Antworten zitieren"
          />
        </div>
      )}
    </SettingsCard>
  );
}
